import { DespesasAttributes } from "@fl/shared/src/types/models/superlogica/despesas";
import boletos from "@mrmgomes/boleto-utils";
import moment from "moment";
import numeral from "numeral";
import {
  IoMdAlert,
  IoMdCheckmarkCircleOutline,
  IoMdCloseCircleOutline,
} from "react-icons/io";
import {
  CHAVES_PIX,
  FORMA_PAGAMENTO_DESPESAS,
} from "../components/select/defaultValuesSL";
import StringsFuncs from "./stringsFuncs";
import { FormatFunctions, ValidateFunctions } from "./validations";

interface paymentStatus {
  method: {
    value: string;
    label: string;
  };
  status: number;
  message: string;
  data: DespesasAttributes;
  element: JSX.Element;
}

export const dadosPagamento = (despesa: DespesasAttributes, grupos: string) => {
  var payment: paymentStatus = {
    data: despesa,
    message: "",
    method: { label: "", value: "" },
    status: -1,
    element: <></>,
  };

  const forma = FORMA_PAGAMENTO_DESPESAS.find(
    (forma) => forma.value == despesa.id_forma_pag?.toString()
  );

  if (forma) {
    switch (forma.label.toLowerCase()) {
      case "boleto":
        payment = checkBoleto(despesa, payment);
        break;

      case "pix":
        payment = checkPix(despesa, payment);
        break;

      case "doc/ted":
        payment = checkDoc(despesa, payment);
        break;

      case "trans. bancária":
        payment = checkTransf(despesa, payment, grupos);
        break;

      case "dinheiro":
        payment = checkDinheiro(despesa, payment);
        break;

      default:
        break;
    }
  }

  const iconSize = 22;
  if (payment.status === 1) {
    payment.element = (
      <div>
        <IoMdCheckmarkCircleOutline size={iconSize} color="green" title="OK" />
        <div>{payment.message}</div>
      </div>
    );
  } else if (payment.status === 2) {
    payment.element = (
      <div>
        <IoMdCloseCircleOutline size={iconSize} color="red" title="Erro" />
        <div>{payment.message}</div>
      </div>
    );
  } else {
    payment.element = (
      <div>
        <IoMdAlert size={iconSize} color="Goldenrod" title="Atenção" />
        <div>{payment.message}</div>
      </div>
    );
  }

  return payment;
};

const checkBoleto = (despesa: DespesasAttributes, payment: paymentStatus) => {
  try {
    if (
      !despesa.st_codigobarras_pdes ||
      despesa.st_codigobarras_pdes.trim() === ""
    ) {
      payment.message = "Sem código de barras";
      payment.status = 2;
    } else {
      const result = boletos.validarBoleto(despesa.st_codigobarras_pdes.trim());
      if (result.sucesso) {
        const valBoleto = parseFloat(result.valor.toString());
        const valDespesa = parseFloat(despesa.vl_valor_pdes!.toString());

        const dateBoleto = moment(result.vencimento).format("DD/MM/YYYY");
        const dateDespesa = moment(
          despesa.dt_vencimento_pdes,
          "MM/DD/YYYY"
        ).format("DD/MM/YYYY");

        if (valBoleto === valDespesa) {
          payment.message = `Código de barras consistente`;
          payment.status = 1;
        } else {
          payment.message = `Valor do código de barras (${numeral(
            valBoleto.toString().replace(".", ",")
          ).format("#,##0.00")}) diferente do lançamento (${numeral(
            valDespesa.toString().replace(".", ",")
          ).format("#,##0.00")})`;
          payment.status = 2;
        }

        if (dateBoleto !== dateDespesa) {
          const dateError = `Vncto. do boleto (${dateBoleto}) diferente do vncto. do lançamento (${dateDespesa}).`;

          if (payment.status === 2) {
            payment.message = `${payment.message}. ${dateError}`;
          } else {
            payment.message = dateError;
            payment.status = 2;
          }
        }
      } else {
        payment.message = "Código de barras inválido";
        payment.status = 2;
      }
    }
  } catch (error) {
    payment.message = "Código de barras inválido";
    payment.status = 2;
  }

  return payment;
};

const checkPix = (despesa: DespesasAttributes, payment: paymentStatus) => {
  if (!despesa.st_pixchave_pdes || despesa.st_pixchave_pdes.trim() === "") {
    payment.message = "Sem chave Pix informada";
    payment.status = 2;
  } else {
    if (despesa.fl_pixtipochave_pdes == 1) {
      if (new ValidateFunctions(despesa.st_pixchave_pdes).cpf()) {
        payment.status = 1;
      } else {
        payment.status = 2;
      }
    } else if (despesa.fl_pixtipochave_pdes == 2) {
      if (new ValidateFunctions(despesa.st_pixchave_pdes).cnpj()) {
        payment.status = 1;
      } else {
        payment.status = 2;
      }
    } else if (despesa.fl_pixtipochave_pdes == 3) {
      if (
        new ValidateFunctions(despesa.st_pixchave_pdes).celular(true, false)
      ) {
        payment.status = 1;
      } else {
        payment.status = 2;
      }
    } else if (despesa.fl_pixtipochave_pdes == 4) {
      if (new ValidateFunctions(despesa.st_pixchave_pdes).email()) {
        payment.status = 1;
      } else {
        payment.status = 2;
      }
    }

    const tipo_chave = CHAVES_PIX.find(
      (i) =>
        i.value.toString() ==
        (despesa.fl_pixtipochave_pdes
          ? despesa.fl_pixtipochave_pdes.toString()
          : "")
    );

    payment.message =
      payment.status === 1
        ? `${tipo_chave?.label}: ${payment.data.st_pixchave_pdes}`
        : `${tipo_chave?.label} inválido(a): ${payment.data.st_pixchave_pdes}`;
  }

  return payment;
};

const checkDoc = (despesa: DespesasAttributes, payment: paymentStatus) => {
  if (
    !despesa.st_banco_fav ||
    !despesa.st_agencia_fav ||
    !despesa.st_contabancaria_fav ||
    !despesa.st_cpfcnpjrecebedor_fav
  ) {
    const messages: string[] = [];
    payment.status = 2;

    if (!despesa.st_banco_fav) {
      messages.push("Banco não informado");
    }

    if (!despesa.st_agencia_fav) {
      messages.push("Agência não informada");
    }

    if (!despesa.st_contabancaria_fav) {
      messages.push("Conta não informada");
    }

    if (!despesa.st_cpfcnpjrecebedor_fav) {
      messages.push("Documento não informado");
    }
    payment.message = messages.join(" | ");
  } else {
    payment.status = 1;
    payment.message = `Banco (${despesa.st_banco_fav}): ${
      despesa.st_nome_banc
    } | Agência: ${despesa.st_agencia_fav} | Conta (${
      despesa.fl_tipoconta_fav === 0 ? "Corrente" : "Poupança"
    }): ${despesa.st_contabancaria_fav} | Documento: ${
      !despesa.st_cpfcnpjrecebedor_fav
        ? ""
        : despesa.st_cpfcnpjrecebedor_fav.length >= 13
          ? new FormatFunctions(despesa.st_cpfcnpjrecebedor_fav).cnpj()
          : new FormatFunctions(despesa.st_cpfcnpjrecebedor_fav).cpf()
    }`;
  }

  return payment;
};

const checkTransf = (
  despesa: DespesasAttributes,
  payment: paymentStatus,
  grupos: string
) => {
  if (
    !despesa.st_banco_fav ||
    !despesa.st_agencia_fav ||
    !despesa.st_contabancaria_fav ||
    !despesa.st_cpfcnpjrecebedor_fav
  ) {
    const messages: string[] = [];
    payment.status = 2;

    if (!despesa.st_banco_fav) {
      messages.push("Banco não informado");
    }

    if (!despesa.st_agencia_fav) {
      messages.push("Agência não informada");
    }

    if (!despesa.st_contabancaria_fav) {
      messages.push("Conta não informada");
    }

    if (!despesa.st_cpfcnpjrecebedor_fav) {
      messages.push("Documento não informado");
    }
    payment.message = messages.join(" | ");
  } else {
    const bancos = ["itau", "bradesco", "santander"];
    const banco_condominio = bancos.find((i) =>
      new StringsFuncs(grupos).removeAccents().toLocaleLowerCase().includes(i)
    );
    const banco_favorecido = new StringsFuncs(despesa.st_nome_banc)
      .removeAccents()
      .toLocaleLowerCase();

    if (
      !despesa.st_nome_banc ||
      (banco_condominio && !banco_favorecido.includes(banco_condominio))
    ) {
      payment.status = 2;
      payment.message =
        "Banco do condomínio diverge do banco do favorecido para ser TRANSFERÊNCIA BANCÁRIA";
    } else {
      payment.status = 1;
      payment.message = `Banco (${despesa.st_banco_fav}): ${
        despesa.st_nome_banc
      } | Agência: ${despesa.st_agencia_fav} | Conta (${
        despesa.fl_tipoconta_fav === 0 ? "Corrente" : "Poupança"
      }): ${despesa.st_contabancaria_fav} | Documento: ${
        !despesa.st_cpfcnpjrecebedor_fav
          ? ""
          : despesa.st_cpfcnpjrecebedor_fav.length >= 13
            ? new FormatFunctions(despesa.st_cpfcnpjrecebedor_fav).cnpj()
            : new FormatFunctions(despesa.st_cpfcnpjrecebedor_fav).cpf()
      }`;
    }
  }

  return payment;
};

const checkDinheiro = (despesa: DespesasAttributes, payment: paymentStatus) => {
  payment.status = 1;
  return payment;
};
